import {
  ChangeEvent,
  FunctionComponent,
  SyntheticEvent,
  useState,
} from "react";
import "react-phone-input-2/lib/style.css";

import PhoneInput from "react-phone-input-2";
import { AuthService } from "../../services/auth";
import { Navigate } from "react-router-dom";
import PasswordInput from "../PasswordInput";
import Label from "../Label";
import { useAsync } from "../../utils/hooks/useAsync";
import Button from "../common/Button";

type Error = {
  data: {
    message: string;
    code: string;
  };
};

type FormData = {
  email: string;
  password: string;
  name: string;
  lastName: string;
  companyName: string;
  phoneNumber: string;
};

type FormValidation = {
  email: boolean;
  password: boolean;
  name: boolean;
  lastName: boolean;
  companyName: boolean;
  phoneNumber: boolean;
};

const AuthForm: FunctionComponent = () => {
  const [formData, setFormData] = useState<FormData>({
    email: "",
    password: "",
    name: "",
    lastName: "",
    companyName: "",
    phoneNumber: "",
  });

  const [formValidation, setFormValidation] = useState<FormValidation>({
    email: true,
    password: true,
    name: true,
    lastName: true,
    companyName: true,
    phoneNumber: true,
  });

  const { email, password, name, phoneNumber, lastName, companyName } =
    formData;
  const { isLoading, isSuccess, isError, error, run } = useAsync<any, Error>();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSignUp = (e: SyntheticEvent) => {
    e.preventDefault();

    const validation = {
      email: /^\S+@\S+\.\S+$/.test(email),
      password: /^(?=.*\d).{6,}$/.test(password),
      name: /^[a-zA-ZÀ-ÖØ-öø-ÿ\s]+$/.test(name),
      lastName: /^[a-zA-ZÀ-ÖØ-öø-ÿ\s]+$/.test(lastName),
      companyName: true, // optional field
      phoneNumber: /^\d+$/.test(phoneNumber),
    };

    const isValid = Object.values(validation).every((isValid) => isValid);
    if (isValid) {
      setFormValidation(validation);
      run(
        AuthService.signUp({
          email,
          password,
          phoneNumber,
          name,
          lastName,
          companyName,
        })
      );
    } else {
      setFormValidation(validation);
    }
  };

  const handlePhoneNumber = (phone: string) => {
    setFormData({ ...formData, phoneNumber: phone });
  };

  return (
    <section className="auth_form_container">
      {isSuccess && <Navigate to="/confirm-account" replace={true} />}
      <form onSubmit={handleSignUp} className="auth_form">
        <>
          <label htmlFor="name">Nombre</label>
          <input
            className={formValidation.name ? "" : "error__input"}
            name="name"
            type="text"
            id="name"
            onChange={handleChange}
          />
          {!formValidation.name && (
            <Label tag="p" variants="body2" className="alert__message">
              Ingresá un nombre para continuar
            </Label>
          )}
          <label htmlFor="lastName">Apellido</label>
          <input
            className={formValidation.lastName ? "" : "error__input"}
            name="lastName"
            type="text"
            id="lastName"
            onChange={handleChange}
          />
          {!formValidation.lastName && (
            <Label tag="p" variants="body2" className="alert__message">
              Ingresá el apellido para continuar
            </Label>
          )}
          <label htmlFor="lastName">Razón social (opcional)</label>
          <input
            className={formValidation.companyName ? "" : "error__input"}
            name="companyName"
            type="text"
            id="companyName"
            onChange={handleChange}
          />
          <label htmlFor="phoneNumber">Teléfono (WhatsApp)</label>
          <PhoneInput
            country={"ar"} // Establece el país inicial (en este caso, Estados Unidos)
            value={phoneNumber}
            inputClass="phoneNumberInput"
            regions={"south-america"}
            autoFormat={false}
            buttonClass="phoneNumberSelect"
            onChange={(phone) => handlePhoneNumber(phone)}
          />
          <p className="phoneNumberFormat">
            Completar usando código de país y área.
          </p>
          <p className="phoneNumberFormat">Ejemplo +54 911 5316 6748</p>
          {!formValidation.phoneNumber && (
            <Label tag="p" variants="body2" className="alert__message">
              Ingresá un teléfono de Whatsapp para continuar
            </Label>
          )}
        </>
        <label htmlFor="email">Email</label>
        <input
          className={formValidation.email ? "" : "error__input"}
          name="email"
          type="text"
          id="email"
          onChange={handleChange}
        />
        {!formValidation.email && (
          <Label tag="p" variants="body2" className="alert__message">
            Email inválido. Chequeá y volvé a intentar
          </Label>
        )}
        <label htmlFor="password">Contraseña*</label>
        <PasswordInput
          handleChange={handleChange}
          validPassword={formValidation.password}
        />
        <Label
          tag="p"
          variants="body2"
          className={
            formValidation.password
              ? "auth_form__register-condition"
              : "alert__message"
          }
        >
          Debe contener al menos 6 caracteres e incluir un número.
        </Label>
        <Button
          isLoading={isLoading}
          className="auth_form__button"
          type="submit"
          id="loginbutton"
        >
          Registrarme
        </Button>
        {isError && (
          <Label tag="p" variants="body2" className="login__error">
            {error?.data?.code === "EMAIL_ALREADY_EXISTS"
              ? "Ya existe un usuario con el mail ingresado"
              : "Hubo un error, intente nuevamente"}
          </Label>
        )}
      </form>
    </section>
  );
};

export default AuthForm;
